import { Component } from '@theme/component';

/**
 * <build-your-routine>
 * Stepped cross-sell modeled on Athena Club's routine module. Clicking a step
 * indicator activates the matching step's copy (left) and image + product card
 * (right). Each step's "Buy Now" adds that single product via the theme's
 * existing product-form-component / add-to-cart-component (AJAX cart), so no cart
 * logic lives here - this component only handles step switching.
 *
 * @extends {Component}
 */
class BuildYourRoutine extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('.routine-steps__option');
    if (option instanceof HTMLElement && option.dataset.optionIndex != null) {
      this.#setActiveOption(option.dataset.stepIndex, option.dataset.optionIndex);
      return;
    }

    const indicator = event.target.closest('.routine-steps__indicator');
    if (!(indicator instanceof HTMLElement) || indicator.dataset.stepIndex == null) return;
    this.#setActiveStep(indicator.dataset.stepIndex);
  };

  /**
   * Activates the step at the given index across the indicators, the step copy,
   * and the image/card column.
   * @param {string} index
   */
  #setActiveStep(index) {
    for (const indicator of this.querySelectorAll('.routine-steps__indicator')) {
      const active = indicator.getAttribute('data-step-index') === index;
      indicator.classList.toggle('is-active', active);
      indicator.setAttribute('aria-selected', active ? 'true' : 'false');
    }

    for (const step of this.querySelectorAll('.routine-steps__step')) {
      step.classList.toggle('is-active', step.getAttribute('data-step-index') === index);
    }

    for (const item of this.querySelectorAll('.routine-steps__image-item')) {
      item.classList.toggle('is-active', item.getAttribute('data-step-index') === index);
    }
  }

  /**
   * Selects which product fills a step. Each option is its own server-rendered
   * view - hero image plus buy card, with correct price, rating and variant id -
   * so this only swaps which view is visible, never rewriting a card's fields.
   * @param {string} stepIndex
   * @param {string} optionIndex
   */
  #setActiveOption(stepIndex, optionIndex) {
    const step = this.querySelector(`.routine-steps__step[data-step-index="${stepIndex}"]`);
    for (const tile of step?.querySelectorAll('.routine-steps__option') ?? []) {
      const active = tile.getAttribute('data-option-index') === optionIndex;
      tile.classList.toggle('is-active', active);
      tile.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    const item = this.querySelector(`.routine-steps__image-item[data-step-index="${stepIndex}"]`);
    for (const view of item?.querySelectorAll('.routine-steps__option-view') ?? []) {
      view.toggleAttribute('hidden', view.getAttribute('data-option-index') !== optionIndex);
    }
  }
}

if (!customElements.get('build-your-routine')) {
  customElements.define('build-your-routine', BuildYourRoutine);
}
